import { BarChart10Icon, Copy01Icon } from '@langchain/untitled-ui-icons';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { KeyedMutator } from 'swr';

import Breadcrumbs from '@/components/Breadcrumbs';
import { DateTimeRangePicker } from '@/components/DateTimeRangePicker/DateTimeRangePicker';
import { DateTimeRangePickerValue } from '@/components/RunsTable/utils/getDateTimeRangeLabel';
import { ScrollSpy } from '@/components/ScrollSpy/ScrollSpy';
import { useScrollSpy } from '@/components/ScrollSpy/hooks/useScrollSpy';
import {
  ScrollSpyElement,
  ScrollSpyState,
} from '@/components/ScrollSpy/hooks/useScrollSpyState';
import { Skeleton } from '@/components/Skeleton';
import { Tooltip } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions';
import { useSingleDashboard } from '@/hooks/useSwr';
import {
  CustomChartExistingInfo,
  CustomChartsSectionSchema,
  RunStatsGroupBy,
  TimedeltaInput,
} from '@/types/schema';

import { PageTitle } from '../../components/PageTitle';
import { DashboardsLayout } from '../Dashboards/components/DashboardsLayout';
import { ChartsGrid } from './ChartsGrid';
import { ChartsCrudPane } from './CustomChartsCrudPane';
import { CloneDashboardDialog } from './components/CloneDashboardDialog';
import { AddButton, SessionLinks } from './components/CustomChartComponents';
import { DashboardGlobalGroupBy } from './components/DashboardGlobalGroupBy';
import { DocsButton } from './components/DocsButton';
import { EditDashboardButton } from './components/EditDashboardButton';
import { DASHBOARD_HEADER_BUTTON_STYLE } from './constants';
import { useChartCrud } from './hooks/useChartCrud';
import { useSearchParamChartDisplayConfig } from './hooks/useSearchParamChartDisplayConfig';
import {
  getUniqueSessionIdsFromChart,
  isDashboardReadOnly,
} from './utils/CustomChartsUtils.utils';
import { MAX_CHARTS_PER_SECTION } from './utils/constants';
import { useSeriesColorsMap } from './utils/getSeriesColor';

const DashboardHeader = ({
  dashboard,
  mutateChartData,
  timeModel,
  setTimeModel,
  chartsExist,
  openExistingChart,
  atMaxCharts,
  isPrebuilt,
  scrollSpyState,
  elements,
  customChartsParams,
  groupBySelection,
  setGroupBySelection,
  isLoading,
  sessionIdsInDashboard,
}: {
  dashboard?: CustomChartsSectionSchema;
  mutateChartData: KeyedMutator<CustomChartsSectionSchema>;
  timeModel: DateTimeRangePickerValue;
  setTimeModel: (value: DateTimeRangePickerValue) => void;
  chartsExist: boolean;
  openExistingChart: (chartData: CustomChartExistingInfo) => void;
  atMaxCharts?: boolean;
  isPrebuilt?: boolean;
  isLoading?: boolean;
  scrollSpyState: ScrollSpyState;
  elements?: ScrollSpyElement[];
  customChartsParams: {
    start_time: string;
    end_time: string;
    stride: TimedeltaInput;
  };
  groupBySelection?: RunStatsGroupBy;
  setGroupBySelection: (groupBySelection?: RunStatsGroupBy) => void;
  sessionIdsInDashboard: string[];
}) => {
  const {
    title,
    description,
    id: dashboardId,
    session_id: sessionId,
  } = dashboard ?? {};
  // if title exists, the dashboard has been loaded once
  const initialLoaded = Boolean(title);

  return (
    <>
      <div className="flex items-center gap-2 px-4">
        <BarChart10Icon size={24} className="mr-2" />
        <PageTitle>
          {isLoading && !initialLoaded ? (
            <Skeleton className="h-6 w-40" />
          ) : (
            title
          )}
        </PageTitle>
        <div className="flex items-center gap-2">
          {!isPrebuilt && dashboard && (
            <EditDashboardButton
              dashboard={dashboard}
              mutateDashboards={mutateChartData}
            />
          )}
          <SessionLinks
            sessionIds={sessionIdsInDashboard}
            hideProjectNames={false}
            buttonClassName={DASHBOARD_HEADER_BUTTON_STYLE}
            variant="dashboard"
          />
          <CloneDashboardDialog dashboardId={dashboardId} sessionId={sessionId}>
            {(setOpen) => (
              <Tooltip
                title="Clone Dashboard"
                side="bottom"
                tooltipClassName="p-0"
              >
                <button
                  type="button"
                  className={DASHBOARD_HEADER_BUTTON_STYLE}
                  onClick={() => setOpen(true)}
                >
                  <Copy01Icon className="size-4" />
                </button>
              </Tooltip>
            )}
          </CloneDashboardDialog>
          <DocsButton link="https://docs.langchain.com/langsmith/dashboards#chart-configuration" />
          {!isPrebuilt && initialLoaded && (
            <Tooltip
              title={
                atMaxCharts
                  ? `Maximum number of charts per dashboard reached (${MAX_CHARTS_PER_SECTION})`
                  : ''
              }
            >
              <div className="py-4">
                <AddButton
                  onClick={() =>
                    openExistingChart({ section_id: dashboardId || null })
                  }
                  text="Chart"
                  variant="solid"
                  disabled={atMaxCharts}
                />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
      {description && (
        <p className="-mt-4 mb-4 max-w-[calc(100vw-400px)] truncate pl-4 text-base text-tertiary">
          {description}
        </p>
      )}
      {chartsExist && (
        <div className="sticky top-0 z-[10] -mt-4 flex flex-wrap justify-between gap-2 bg-primary px-4 py-4">
          {elements && scrollSpyState.needsScrollSpy ? (
            <ScrollSpy
              {...scrollSpyState}
              elements={elements}
              horizontal
              elementClassName="px-3 py-1.5"
            />
          ) : (
            <div />
          )}

          <div className="flex gap-2">
            {sessionId && timeModel && (
              <DashboardGlobalGroupBy
                sessionId={sessionId}
                customChartsParams={customChartsParams}
                groupBySelection={groupBySelection}
                setGroupBySelection={setGroupBySelection}
              />
            )}
            <DateTimeRangePicker
              value={timeModel}
              onChange={setTimeModel}
              hideAllTime
              align="end"
              context="dashboard"
              triggerClassName="bg-primary text-xs text-primary hover:bg-secondary"
            />
          </div>
        </div>
      )}
    </>
  );
};

const SingleDashboard = () => {
  const { authorize } = usePermissions();
  const { dashboardId } = useParams();

  const { isOpen, chartData, openExistingChart, closeChartCrudPane } =
    useChartCrud();

  const {
    timeModel,
    setTimeModel,
    groupBySelection,
    setGroupBySelection,
    customChartsParams,
  } = useSearchParamChartDisplayConfig('dashboard', { includeGroupBy: true });

  const {
    data: dashboard,
    mutate: mutateChartData,
    isLoading,
    error,
  } = useSingleDashboard(
    dashboardId,
    {
      ...customChartsParams,
      group_by: groupBySelection,
    },
    { keepPreviousData: true }
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [chartsRendered, setChartsRendered] = useState(false);

  const isPrebuilt = isDashboardReadOnly(dashboard);
  const sections = useMemo(() => {
    if (!isPrebuilt || !dashboard?.charts) return undefined;

    const sectionsModel = dashboard.sub_sections ?? [];

    return sectionsModel.map((section) => ({
      id: section.id,
      title: section.title,
    }));
  }, [dashboard?.charts, dashboard?.sub_sections, isPrebuilt]);

  // This is a hack to make sure the scroll spy renders if there are charts and the screen is scrollable.
  // For some reason, we were seeing a flaky issue where the scroll spy wasn't rendered, even though the
  // isVisible value seems to be correct. This bug doesn't occur in any of the other places where the scroll
  // spy is used, but I spent enough time trying to debug it that it seems to be not worth my time to spend
  // any more on it, leaving us with this useEffect.
  useEffect(() => {
    if (
      isPrebuilt &&
      (!!dashboard?.charts?.length || !!dashboard?.sub_sections?.length) &&
      !isLoading &&
      !!sections &&
      !!sections.length
    ) {
      const timer = setTimeout(() => {
        setChartsRendered(true);
      }, 0);
      return () => clearTimeout(timer);
    } else {
      setChartsRendered(false);
    }
  }, [
    dashboard?.charts,
    dashboard?.sub_sections?.length,
    isLoading,
    isPrebuilt,
    sections,
  ]);

  const { scrollSpyState, sectionRefs, scrollSpyElements } = useScrollSpy({
    scrollContainerRef,
    isElementVisible: chartsRendered,
    sections,
    scrollOffset: 60,
  });

  const sessionIdsInDashboard = useMemo(() => {
    if (!dashboard) return [];

    const chartIds =
      dashboard.charts?.flatMap(getUniqueSessionIdsFromChart) || [];
    const subsectionIds =
      dashboard.sub_sections?.flatMap((section) =>
        section.charts.flatMap(getUniqueSessionIdsFromChart)
      ) || [];

    return Array.from(new Set([...chartIds, ...subsectionIds]));
  }, [dashboard]);

  const hideProjectNames = sessionIdsInDashboard.length < 2;

  const seriesColorsMap = useSeriesColorsMap({
    dashboard,
    isPrebuilt,
  });

  if (!authorize('charts:read')) {
    return null;
  }

  return (
    <DashboardsLayout
      key={dashboardId}
      handleScroll={scrollSpyState.handleScroll}
      ref={scrollContainerRef}
    >
      <div className="pt-3" />
      <div className="px-4">
        <Breadcrumbs />
      </div>
      <DashboardHeader
        dashboard={dashboard}
        mutateChartData={mutateChartData}
        isLoading={isLoading}
        timeModel={timeModel}
        setTimeModel={setTimeModel}
        customChartsParams={customChartsParams}
        chartsExist={
          !!dashboard?.charts?.length || !!dashboard?.sub_sections?.length
        }
        openExistingChart={openExistingChart}
        isPrebuilt={isPrebuilt}
        scrollSpyState={scrollSpyState}
        elements={scrollSpyElements}
        groupBySelection={groupBySelection}
        setGroupBySelection={setGroupBySelection}
        sessionIdsInDashboard={sessionIdsInDashboard}
      />
      <ChartsGrid
        charts={dashboard?.charts ?? []}
        sub_sections={dashboard?.sub_sections}
        dashboardId={dashboardId}
        isLoading={isLoading}
        mutateChartData={mutateChartData}
        openExistingChart={openExistingChart}
        timeFilter={{
          timeModel,
          setTimeModel,
          customChartsParams,
        }}
        error={error}
        isPrebuilt={isPrebuilt}
        sectionRefs={sectionRefs}
        groupBySelection={groupBySelection}
        hideProjectNames={hideProjectNames}
        seriesColorsMap={seriesColorsMap}
      />

      <ChartsCrudPane
        key={chartData?.id}
        open={isOpen}
        onSuccess={() => {
          mutateChartData();
        }}
        onClose={closeChartCrudPane}
        existingChart={chartData}
        seriesColorsMap={seriesColorsMap}
      />
    </DashboardsLayout>
  );
};

export default SingleDashboard;
